import axios from "axios";

// Utility for API base URL
export const API_BASE =
  process.env.REACT_APP_BASE_URL || "https://docfidb.docfico.com/api/v1";

// Token utilities
export function setAuthToken(token: string) {
  localStorage.setItem("authToken", token);
}

export function getAuthToken(): string | null {
  return localStorage.getItem("authToken");
}

export function removeAuthToken() {
  localStorage.removeItem("authToken");
}

// Refresh handler — registered by authService to avoid circular imports
type RefreshHandler = () => Promise<string>;
let _refreshHandler: RefreshHandler | null = null;

export function setRefreshHandler(handler: RefreshHandler) {
  _refreshHandler = handler;
}

// Axios request interceptor
axios.interceptors.request.use((config) => {
  const token = getAuthToken();
  config.headers = config.headers || {};
  // Let the browser set multipart/form-data (with boundary) for FormData bodies;
  // only force JSON for regular payloads.
  if (!(config.data instanceof FormData)) {
    config.headers["Content-Type"] = "application/json";
  } else {
    delete config.headers["Content-Type"];
  }
  if (token) {
    config.headers["Authorization"] = `Bearer ${token}`;
  }
  return config;
});

// Axios response interceptor — silent JWT refresh on 401
let isRefreshing = false;
let pendingQueue: Array<{ resolve: (token: string) => void; reject: (err: any) => void }> = [];

const drainQueue = (error: any, token?: string) => {
  pendingQueue.forEach((p) => (error ? p.reject(error) : p.resolve(token!)));
  pendingQueue = [];
};

axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config;
    const status = error.response?.status;
    if ((status !== 401 && status !== 403) || original._retry || !_refreshHandler) {
      return Promise.reject(error);
    }

    if (isRefreshing) {
      return new Promise<string>((resolve, reject) => {
        pendingQueue.push({ resolve, reject });
      }).then((token) => {
        original.headers["Authorization"] = `Bearer ${token}`;
        return axios(original);
      });
    }

    original._retry = true;
    isRefreshing = true;

    try {
      const newToken = await _refreshHandler();
      drainQueue(null, newToken);
      original.headers["Authorization"] = `Bearer ${newToken}`;
      return axios(original);
    } catch (refreshError) {
      drainQueue(refreshError);
      removeAuthToken();
      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  }
);
